import libAxios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';

import { ContentTypes, getResponseCamelization } from '../helpers/api';
import { RequestContext, RequestFn } from '../types/api';
import { RequestMethod } from '../types/common';
import { REACT_APP_API_URL } from 'helpers/conf';
import { API_PATH } from 'helpers/constants';
import { delay } from '../helpers';

function getStaticGlobalHeaders() {
  const headers: { [name: string]: string } = {};

  headers['Accept'] = ContentTypes.vndJson;
  headers['Content-Type'] = ContentTypes.json;

  return headers;
}

const axiosGlobalConfig: AxiosRequestConfig = {
  baseURL: `${REACT_APP_API_URL}${API_PATH}`,
  timeout: parseInt(process.env.REACT_APP_REQUEST_TIMEOUT || '20') * 1000,
  headers: getStaticGlobalHeaders(),
};

axiosGlobalConfig.transformResponse = [getResponseCamelization()];
axiosGlobalConfig.paramsSerializer = (params) =>
  qs.stringify(params, { arrayFormat: 'repeat' });
axiosGlobalConfig.withCredentials = true;

const axios = libAxios.create(axiosGlobalConfig);

function getDynamicGlobalHeaders() {
  const headers: { [name: string]: string } = {};

  return headers;
}

const errorStatuses = [400, 404, 500];

axios.interceptors.response.use(
  (response) => {
    if (errorStatuses.includes(response.status)) {
      return response;
    }

    if (response.data.errors != null) {
      return Promise.reject(response);
    }

    return response;
  },
  (error) => {
    if (error.response != null) {
    }

    return Promise.reject(error.response);
  }
);

export function request<T = any>(
  this: AxiosRequestConfig | void,
  method: RequestMethod,
  uri: string,
  data?: object,
  functionScopedConfig?: AxiosRequestConfig,
  mockData?: { data: T; timeout: number },
  retryCount = 3,
  retryDelay = 2000
): Promise<AxiosResponse<T>> {
  const conf: AxiosRequestConfig = {
    method,
    url: uri,
    [method === 'get' ? 'params' : 'data']: data,
    ...this,
    ...functionScopedConfig,
  };

  const dynamicGlobalHeaders = getDynamicGlobalHeaders();

  if (conf.headers == null) {
    conf.headers = dynamicGlobalHeaders;
  } else {
    conf.headers = { ...dynamicGlobalHeaders, ...conf.headers };
  }

  if (mockData != null) {
    return new Promise<AxiosResponse<T>>((resolve) => {
      setTimeout(() => {
        resolve({
          data: mockData.data,
          status: 200,
          statusText: 'OK',
          headers: dynamicGlobalHeaders,
          config: conf,
        });
      }, mockData.timeout);
    });
  }

  const makeRequest = async (attempt: number): Promise<AxiosResponse<T>> => {
    try {
      return await axios(conf);
    } catch (error: any) {
      if (attempt < retryCount && error.status >= 500) {
        console.warn(
          `Request failed with status ${error.response?.status}. Retrying ${
            attempt + 1
          }/${retryCount}...`
        );
        await delay(retryDelay);
        return makeRequest(attempt + 1);
      }
      throw error;
    }
  };

  return makeRequest(0);
}

export function factory<Args extends any[], Return>(
  handler: (r: typeof request) => RequestFn<Args, Return>
) {
  return function (this: RequestContext, ...rest: Args) {
    const r = request.bind(this) as typeof request; // bind looses generic

    return handler(r)(...rest);
  };
}
